/**
 * @fileoverview Collapsible Dropdown Component
 * A reusable custom element for collapsible content sections (FAQ, guarantees, etc.)
 * 
 * 📡 EVENT SUMMARY:
 * 
 * EVENTS DISPATCHED:
 * - dropdown:opened  → Emitted when dropdown opens
 * - dropdown:closed  → Emitted when dropdown closes
 * - dropdown:toggled → Emitted when dropdown state changes
 * - dropdown:ready   → Emitted when component is initialized
 * 
 * EVENT DATA STRUCTURE:
 * - dropdown:opened/closed/toggled: { 
 *     isOpen: boolean, 
 *     timestamp: number,
 *     element: HTMLElement,
 *     trigger: HTMLElement,
 *     content: HTMLElement
 *   }
 * - dropdown:ready: {
 *     element: HTMLElement,
 *     config: Object
 *   }
 */

import { DOM, Debug, Performance } from '../cart/utils/index.js';

export class CollapsibleDropdown extends HTMLElement {
  /**
   * CollapsibleDropdown class constructor
   */
  constructor() {
    super();
    
    this.isOpen = false;
    this.elements = {};
    this.config = {
      animationDuration: 300,
      autoOpen: false,
      triggerSelector: '[data-dropdown-trigger]',
      contentSelector: '[data-dropdown-content]',
      caretSelector: '[data-dropdown-caret]',
      innerSelector: '.dropdown__inner',
      ...this.getConfigFromAttributes()
    };

    // Bind methods to preserve context
    this.toggle = this.toggle.bind(this);
    this.open = this.open.bind(this);
    this.close = this.close.bind(this);
    this.handleKeydown = this.handleKeydown.bind(this);
    this.handleClickOutside = this.handleClickOutside.bind(this);

    this.init();
  }

  /**
   * Get configuration from data attributes
   * @private
   */
  getConfigFromAttributes() {
    const config = {};
    
    // Auto-open configuration
    config.autoOpen = this.hasAttribute('data-auto-open');
    
    // Animation duration
    const duration = this.getAttribute('data-animation-duration');
    if (duration) config.animationDuration = parseInt(duration) || 300;
    
    // Custom selectors
    const triggerSelector = this.getAttribute('data-trigger-selector');
    if (triggerSelector) config.triggerSelector = triggerSelector;
    
    const contentSelector = this.getAttribute('data-content-selector');
    if (contentSelector) config.contentSelector = contentSelector;
    
    const caretSelector = this.getAttribute('data-caret-selector');
    if (caretSelector) config.caretSelector = caretSelector;
    
    // Close on outside click
    config.closeOnOutsideClick = this.hasAttribute('data-close-outside');
    
    return config;
  }

  /**
   * Initialize the component
   * @private
   */
  init() {
    Debug.log('info', '[CollapsibleDropdown] Initializing component');
    
    Performance.measure(() => {
      this.cacheElements();
      this.setupAccessibility();
      this.bindEvents();
      this.setInitialState();
      this.dispatchReadyEvent();
    }, 'collapsibleDropdownInit');
  }

  /**
   * Cache DOM elements
   * @private
   */
  cacheElements() {
    this.elements = {
      trigger: this.querySelector(this.config.triggerSelector),
      content: this.querySelector(this.config.contentSelector),
      caret: this.querySelector(this.config.caretSelector),
      inner: this.querySelector(this.config.innerSelector)
    };

    // Validate required elements
    if (!this.elements.trigger || !this.elements.content) {
      throw new Error('CollapsibleDropdown: Required elements not found (trigger and content are required)');
    }

    Debug.log('info', '[CollapsibleDropdown] Elements cached', {
      trigger: !!this.elements.trigger,
      content: !!this.elements.content,
      caret: !!this.elements.caret,
      inner: !!this.elements.inner
    });
  }

  /**
   * Setup accessibility features
   * @private
   */
  setupAccessibility() {
    // Generate unique IDs if not present
    if (!this.elements.content.id) {
      this.elements.content.id = `dropdown-content-${Date.now()}`;
    }
    if (!this.elements.trigger.id) {
      this.elements.trigger.id = `dropdown-trigger-${Date.now()}`;
    }

    // Set ARIA attributes
    this.elements.trigger.setAttribute('aria-expanded', this.config.autoOpen ? 'true' : 'false');
    this.elements.trigger.setAttribute('aria-controls', this.elements.content.id);
    this.elements.trigger.setAttribute('role', 'button');
    
    // Add role to content
    this.elements.content.setAttribute('role', 'region');
    this.elements.content.setAttribute('aria-labelledby', this.elements.trigger.id);
    
    // Ensure trigger is focusable
    if (!this.elements.trigger.hasAttribute('tabindex')) {
      this.elements.trigger.setAttribute('tabindex', '0');
    }
  }

  /**
   * Bind event listeners
   * @private
   */
  bindEvents() {
    Debug.log('info', '[CollapsibleDropdown] Binding events');

    // Click event for trigger
    this.elements.trigger.addEventListener('click', this.toggle);

    // Keyboard events
    this.elements.trigger.addEventListener('keydown', this.handleKeydown);

    // Focus management
    this.elements.trigger.addEventListener('focus', () => {
      this.classList.add('focused');
    });

    this.elements.trigger.addEventListener('blur', () => {
      this.classList.remove('focused');
    });

    // Close on outside click if enabled
    if (this.config.closeOnOutsideClick) {
      document.addEventListener('click', this.handleClickOutside);
    }
  }

  /**
   * Handle clicks outside the dropdown
   * @param {Event} event
   * @private
   */
  handleClickOutside(event) {
    if (this.isOpen && !this.contains(event.target)) {
      this.close();
    }
  }

  /**
   * Set initial state based on configuration
   * @private
   */
  setInitialState() {
    console.log("Initial state")
    console.log(this.config)
    if (this.config.autoOpen) {
      this.open();
    } else {
      this.close();
    }
  }

  /**
   * Dispatch ready event
   * @private
   */
  dispatchReadyEvent() {
    this.dispatchEvent(new CustomEvent('dropdown:ready', {
      detail: {
        element: this,
        config: this.config
      },
      bubbles: true
    }));
  }

  /**
   * Handle keyboard events
   * @param {KeyboardEvent} event
   * @private
   */
  handleKeydown(event) {
    switch (event.key) {
      case 'Enter':
      case ' ':
        event.preventDefault();
        this.toggle();
        break;
      case 'Escape':
        if (this.isOpen) {
          event.preventDefault();
          this.close();
          this.elements.trigger.focus();
        }
        break;
    }
  }

  /**
   * Toggle dropdown state
   * @param {Event} event - Optional event object
   */
  toggle(event) {
    if (event) {
      event.preventDefault();
    }

    Performance.measure(() => {
      if (this.isOpen) {
        this.close();
      } else {
        this.open();
      }
    }, 'collapsibleDropdownToggle');

    // Dispatch toggle event
    this.dispatchEvent(new CustomEvent('dropdown:toggled', {
      detail: {
        isOpen: this.isOpen,
        timestamp: Date.now(),
        element: this,
        trigger: this.elements.trigger,
        content: this.elements.content
      },
      bubbles: true
    }));
  }

  /**
   * Open the dropdown
   */
  open() {
    if (this.isOpen) return;

    Debug.log('info', '[CollapsibleDropdown] Opening dropdown');

    this.isOpen = true;
    this.classList.add('is-open');

    // Update ARIA attributes
    this.elements.trigger.setAttribute('aria-expanded', 'true');

    // Animate content
    this.animateContent(true);

    // Rotate caret if available
    if (this.elements.caret) {
      this.elements.caret.classList.add('rotate-180');
    }

    // Dispatch open event
    this.dispatchEvent(new CustomEvent('dropdown:opened', {
      detail: {
        isOpen: this.isOpen,
        timestamp: Date.now(),
        element: this,
        trigger: this.elements.trigger,
        content: this.elements.content
      },
      bubbles: true
    }));
  }

  /**
   * Close the dropdown
   */
  close() {
    if (!this.isOpen) return;

    Debug.log('info', '[CollapsibleDropdown] Closing dropdown');

    this.isOpen = false;
    this.classList.remove('is-open');

    // Update ARIA attributes
    this.elements.trigger.setAttribute('aria-expanded', 'false');

    // Animate content
    this.animateContent(false);

    // Rotate caret if available
    if (this.elements.caret) {
      this.elements.caret.classList.remove('rotate-180');
    }

    // Dispatch close event
    this.dispatchEvent(new CustomEvent('dropdown:closed', {
      detail: {
        isOpen: this.isOpen,
        timestamp: Date.now(),
        element: this,
        trigger: this.elements.trigger,
        content: this.elements.content
      },
      bubbles: true
    }));
  }

  /**
   * Animate content visibility
   * @param {boolean} isOpening - Whether the dropdown is opening
   * @private
   */
  animateContent(isOpening) {
    const content = this.elements.content;
    const inner = this.elements.inner;

    console.log("OPENING")
    if (isOpening) {
      // Show content
      content.style.maxHeight = '35rem';
      content.style.opacity = '1';
      
      if (inner) {
        inner.style.display = '';
      }
    } else {
      // Hide content
      content.style.maxHeight = '0';
      content.style.opacity = '0';
      
      if (inner) {
        inner.style.display = 'none';
      }
    }
  }

  /**
   * Get current state
   * @returns {Object} Current state
   */
  getState() {
    return {
      isOpen: this.isOpen,
      config: this.config,
      elements: {
        trigger: this.elements.trigger,
        content: this.elements.content,
        caret: this.elements.caret,
        inner: this.elements.inner
      }
    };
  }

  /**
   * Update configuration
   * @param {Object} newConfig - New configuration
   */
  updateConfig(newConfig) {
    this.config = { ...this.config, ...newConfig };
    Debug.log('info', '[CollapsibleDropdown] Configuration updated', this.config);
  }

  /**
   * Clean up component
   */
  destroy() {
    Debug.log('info', '[CollapsibleDropdown] Destroying component');

    // Remove event listeners
    this.elements.trigger.removeEventListener('click', this.toggle);
    this.elements.trigger.removeEventListener('keydown', this.handleKeydown);
    
    if (this.config.closeOnOutsideClick) {
      document.removeEventListener('click', this.handleClickOutside);
    }

    // Remove classes
    this.classList.remove('is-open', 'focused');
    
    // Dispatch destroy event
    this.dispatchEvent(new CustomEvent('dropdown:destroyed', {
      detail: {
        element: this,
        timestamp: Date.now()
      },
      bubbles: true
    }));
  }
}

// Register custom element
if (!customElements.get('collapsible-dropdown')) {
  customElements.define('collapsible-dropdown', CollapsibleDropdown);
}

export default CollapsibleDropdown; 